import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Pagination,
  Popconfirm,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { DownloadOutlined, EyeOutlined, InboxOutlined, PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { executeBatch, exportRunResult, getRunDetail, getRunHistory, uploadWeeklyCsv } from '../../../api/operations';
import styles from './index.module.scss';

const { Dragger } = Upload;
const { Text, Title } = Typography;

const STATUS_META: Record<number, { label: string; color?: string }> = {
  0: { label: '待确认', color: 'warning' },
  1: { label: '已执行', color: 'success' },
  2: { label: '部分成功', color: 'warning' },
  3: { label: '失败', color: 'error' },
  4: { label: '已作废' },
};

const matchStatusText: Record<number, string> = {
  0: '可更新',
  1: '新增商品提示',
  2: '名称重复',
  3: '字段非法',
};

const itemColumns = [
  { title: '行号', dataIndex: 'rowNo', width: 72 },
  { title: '商品名称', dataIndex: 'productName', width: 220, ellipsis: true },
  { title: '库存', dataIndex: 'weeklyStock', width: 100 },
  { title: '上下架', dataIndex: 'shelfStatus', width: 100 },
  { title: '执行前库存', dataIndex: 'oldStock', width: 110 },
  { title: '执行前状态', dataIndex: 'oldStatus', width: 110 },
  {
    title: '积分/价格（仅提示）',
    key: 'previewValues',
    width: 180,
    render: (_: unknown, row: any) => `${row.newPoints ?? '-'} / ${row.newPrice ?? '-'}`,
  },
  {
    title: '结果',
    key: 'result',
    width: 140,
    render: (_: unknown, row: any) => (
      <Space
        direction="vertical"
        size={0}>
        <Tag color={row.matchStatus === 0 ? 'green' : row.matchStatus === 1 ? 'blue' : 'red'}>
          {matchStatusText[row.matchStatus] || '未知'}
        </Tag>
        {row.failReason && <Text type="danger">{row.failReason}</Text>}
      </Space>
    ),
  },
];

const resultColumns = [
  { title: '实例ID', dataIndex: 'instanceId', width: 120 },
  { title: '商品名称', dataIndex: 'productName', width: 220 },
  {
    title: '结果',
    dataIndex: 'success',
    width: 100,
    render: (success: boolean) => <Tag color={success ? 'success' : 'error'}>{success ? '成功' : '失败'}</Tag>,
  },
  { title: '失败原因', dataIndex: 'failReason' },
];

const WeeklyExchange: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [preview, setPreview] = useState<any>(null);
  const [executeResult, setExecuteResult] = useState<any>(null);
  const [historyRows, setHistoryRows] = useState<any[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState({ pageNum: 1, pageSize: 10 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadHistory = async (page = historyPage) => {
    setHistoryLoading(true);
    try {
      const res: any = await getRunHistory(page);
      if (res?.code === 200) {
        setHistoryRows(res.data?.rows || []);
        setHistoryTotal(res.data?.total || 0);
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyPage.pageNum, historyPage.pageSize]);

  const uploadProps: UploadProps = useMemo(
    () => ({
      accept: '.csv',
      multiple: false,
      fileList,
      beforeUpload: file => {
        const isCsv = file.name.toLowerCase().endsWith('.csv');
        if (!isCsv) {
          message.error('仅支持 CSV 文件');
          return Upload.LIST_IGNORE;
        }
        setFileList([file]);
        return false;
      },
      onRemove: () => setFileList([]),
    }),
    [fileList],
  );

  const submitUpload = async () => {
    const file = fileList[0]?.originFileObj;
    if (!file) {
      message.warning('请先选择 CSV 文件');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const res: any = await uploadWeeklyCsv(formData);
      if (res?.code === 200) {
        setPreview(res.data);
        setCurrent(1);
        await loadHistory({ pageNum: 1, pageSize: historyPage.pageSize });
        setHistoryPage(old => ({ ...old, pageNum: 1 }));
        message.success('上传并解析成功');
      }
    } finally {
      setUploading(false);
    }
  };

  const execute = async () => {
    if (!preview?.batchNo) return;
    setUploading(true);
    try {
      const res: any = await executeBatch(preview.batchNo);
      if (res?.code === 200) {
        setExecuteResult(res.data);
        setCurrent(2);
        await loadHistory({ pageNum: 1, pageSize: historyPage.pageSize });
        setHistoryPage(old => ({ ...old, pageNum: 1 }));
      }
    } finally {
      setUploading(false);
    }
  };

  const openDetail = async (runId: number) => {
    const res: any = await getRunDetail(runId);
    if (res?.code === 200) {
      setDetail(res.data);
      setDetailOpen(true);
    }
  };

  const resetFlow = () => {
    setCurrent(0);
    setFileList([]);
    setPreview(null);
    setExecuteResult(null);
  };

  const previewTables = [
    { title: '可更新', rows: preview?.matchedItems || [], empty: '没有可更新行' },
    { title: '异常', rows: preview?.failedItems || [], empty: '没有异常行' },
    { title: '新增商品提示', rows: preview?.newProductItems || [], empty: '没有新增商品提示' },
  ];

  return (
    <div className={styles.page}>
      <Title level={3}>EC兑换周更</Title>
      <Card className={styles.flowCard}>
        <Steps
          current={current}
          items={[{ title: '上传' }, { title: '预览' }, { title: '执行结果' }]}
        />
      </Card>

      {current === 0 && (
        <Card
          title="上传 ACX 物料清单 CSV"
          className={styles.card}>
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽 CSV 文件到此区域</p>
            <p className="ant-upload-hint">支持 UTF-8（含 BOM）或 GB18030；重复文件允许再次上传并会标记历史批次。</p>
          </Dragger>
          <div className={styles.actions}>
            <Button
              type="primary"
              loading={uploading}
              onClick={submitUpload}>
              上传并预览
            </Button>
          </div>
        </Card>
      )}

      {current === 1 && preview && (
        <Card
          title={`批次 ${preview.batchNo}`}
          className={styles.card}>
          {preview.duplicateOfBatchNo && (
            <Alert
              type="warning"
              showIcon
              message={`本次文件与历史批次 ${preview.duplicateOfBatchNo} 相同`}
              description="重复上传不会被拦截；新批次上传成功后，所有待确认旧批次会自动作废。"
              className={styles.alert}
            />
          )}
          <Descriptions
            size="small"
            column={3}
            className={styles.summary}>
            <Descriptions.Item label="文件名">{preview.fileName}</Descriptions.Item>
            <Descriptions.Item label="总行数">{preview.totalRows}</Descriptions.Item>
            <Descriptions.Item label="积分/价格">仅预览提示，不写入业务表</Descriptions.Item>
          </Descriptions>
          {previewTables.map(table => (
            <section
              key={table.title}
              className={styles.section}>
              <Title level={5}>
                {table.title}（{table.rows.length}）
              </Title>
              {table.rows.length ? (
                <Table
                  rowKey={(row: any) => `${row.rowNo}-${row.productName}`}
                  columns={itemColumns}
                  dataSource={table.rows}
                  pagination={{ pageSize: 8 }}
                  scroll={{ x: 1100 }}
                />
              ) : (
                <Empty description={table.empty} />
              )}
            </section>
          ))}
          <div className={styles.actions}>
            <Button onClick={resetFlow}>重新上传</Button>
            <Popconfirm
              title="确认执行该批次？"
              description="仅执行唯一匹配且合法、库存非空的行。"
              onConfirm={execute}
              okText="确认执行"
              cancelText="取消">
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                loading={uploading}>
                确认执行
              </Button>
            </Popconfirm>
          </div>
        </Card>
      )}

      {current === 2 && executeResult && (
        <Card
          title={`批次 ${executeResult.batchNo} 执行结果`}
          className={styles.card}>
          <Descriptions
            bordered
            column={4}>
            <Descriptions.Item label="状态">
              <Tag color={STATUS_META[executeResult.status]?.color}>{STATUS_META[executeResult.status]?.label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="总执行行数">{executeResult.totalRows}</Descriptions.Item>
            <Descriptions.Item label="成功">{executeResult.successRows}</Descriptions.Item>
            <Descriptions.Item label="失败">{executeResult.failedRows}</Descriptions.Item>
          </Descriptions>
          <Divider />
          {executeResult.failedDetails?.length ? (
            <Table
              rowKey={(row: any) => `${row.instanceId}-${row.productName}`}
              columns={resultColumns}
              dataSource={executeResult.failedDetails}
              pagination={{ pageSize: 8 }}
            />
          ) : (
            <Empty description="没有失败明细" />
          )}
          <div className={styles.actions}>
            <Button
              type="primary"
              onClick={resetFlow}>
              继续上传
            </Button>
          </div>
        </Card>
      )}

      <Card
        title="历史记录"
        className={styles.card}
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadHistory()}
            loading={historyLoading}>
            刷新
          </Button>
        }>
        <Table
          rowKey="id"
          loading={historyLoading}
          dataSource={historyRows}
          pagination={false}
          scroll={{ x: 1100 }}
          columns={[
            { title: '批次号', dataIndex: 'batchNo', width: 190 },
            { title: '文件名', dataIndex: 'fileName', width: 220, ellipsis: true },
            { title: '操作人', dataIndex: 'operator', width: 110 },
            { title: '上传时间', dataIndex: 'uploadTime', width: 180 },
            { title: '总行数', dataIndex: 'totalRows', width: 90 },
            {
              title: '状态',
              dataIndex: 'status',
              width: 110,
              render: (status: number) => (
                <Tag color={STATUS_META[status]?.color}>{STATUS_META[status]?.label || status}</Tag>
              ),
            },
            {
              title: '操作',
              key: 'actions',
              width: 180,
              render: (_: unknown, row: any) => (
                <Space>
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => openDetail(row.id)}>
                    详情
                  </Button>
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => exportRunResult(row.id)}>
                    导出
                  </Button>
                </Space>
              ),
            },
          ]}
        />
        <div className={styles.pagination}>
          <Pagination
            current={historyPage.pageNum}
            pageSize={historyPage.pageSize}
            total={historyTotal}
            showSizeChanger
            onChange={(pageNum, pageSize) => setHistoryPage({ pageNum, pageSize })}
          />
        </div>
      </Card>

      <Drawer
        title={`运行详情${detail?.record?.batchNo ? ` - ${detail.record.batchNo}` : ''}`}
        width={900}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}>
        {detail?.record && (
          <Descriptions
            column={2}
            size="small">
            <Descriptions.Item label="文件名">{detail.record.fileName}</Descriptions.Item>
            <Descriptions.Item label="操作人">{detail.record.operator}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={STATUS_META[detail.record.status]?.color}>{STATUS_META[detail.record.status]?.label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="重复来源">{detail.record.duplicateOfBatchNo || '无'}</Descriptions.Item>
          </Descriptions>
        )}
        <Table
          rowKey="id"
          dataSource={detail?.items || []}
          columns={itemColumns}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1100 }}
        />
      </Drawer>
    </div>
  );
};

export default WeeklyExchange;
